import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/dashboard/Dashboard';
import Hospitals from './pages/hospitals/Hospitals';
import Patients from './pages/patients/Patients';
import PatientProfile from './pages/patients/PatientProfile';
import Appointments from './pages/appointments/Appointments';
import Inventory from './pages/inventory/Inventory';
import Billing from './pages/billing/Billing';
import Staff from './pages/staff/Staff';

const theme = createTheme({
  palette: {
    primary: { main: '#1e40af' },
    secondary: { main: '#0284c7' },
    background: { default: '#f0f4f8' }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", -apple-system, sans-serif',
    h5: { fontWeight: 800 },
    h6: { fontWeight: 700 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 } } },
    MuiCard: { styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } } },
    MuiTableCell: { styleOverrides: { root: { borderBottom: '1px solid #f1f5f9' } } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
  }
});

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const AppRoutes = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="hospitals" element={<Hospitals />} />
        <Route path="patients" element={<Patients />} />
        <Route path="patients/:id" element={<PatientProfile />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="billing" element={<Billing />} />
        <Route path="staff" element={<Staff />} />
      </Route>
    </Routes>
  </BrowserRouter>
);

const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Toaster position="top-right" toastOptions={{ style: { borderRadius: 10, fontFamily: 'Inter, sans-serif', fontSize: 14 } }} />
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
